/** Shared UI state and helpers for the panel renderers. */

import { resource, type Ingredient } from '../content/resources';
import type { Workshop } from '../sim/actions';
import type { FleetStats } from '../sim/stats';
import type { GameState } from '../sim/state';
import { esc, fmtNum } from './format';

export type Tab = 'planets' | 'production' | 'market' | 'projects' | 'fold';
export type HoldView = 'ore' | 'alloy' | 'item';
/** The Station (between-galaxies hub, like IdleVoid's): what you keep across Folds. */
export type StationTab = 'hub' | 'rooms' | 'ships' | 'matrix' | 'crew' | 'challenge' | 'record' | 'codex' | 'fittings';
export type Screen = 'galaxy' | 'station';

/** What the last Fold sold, shown at the top of the Station until you launch. */
export interface FoldReport {
  readonly value: number;
  readonly depth: number;
  readonly te: number;
  readonly sc: number;
}
export type BuyMode = 1 | 10 | 'max';

/** The bottom sheet: one at a time, over the panel. */
export type Sheet =
  | { readonly kind: 'recipe'; readonly workshop: Workshop; readonly slot: number }
  | { readonly kind: 'node'; readonly id: string }
  | { readonly kind: 'assign'; readonly planet: number }
  | { readonly kind: 'ship'; readonly id: string };

export interface MatrixCamera {
  x: number;
  y: number;
  /** World units visible across the shorter side. */
  span: number;
}

export interface UiState {
  tab: Tab;
  selected: number;
  buyMode: BuyMode;
  sheet: Sheet | null;
  holdView: HoldView;
  screen: Screen;
  stationTab: StationTab;
  foldReport: FoldReport | null;
  foldDrift: string | null;
  camera: MatrixCamera;
  devSpeed: number;
}

export interface View {
  readonly state: GameState;
  readonly stats: FleetStats;
  readonly ui: UiState;
}

export function newUiState(): UiState {
  return {
    tab: 'planets',
    selected: 0,
    buyMode: 1,
    sheet: null,
    holdView: 'ore',
    screen: 'galaxy',
    stationTab: 'hub',
    foldReport: null,
    foldDrift: null,
    camera: { x: 0, y: 0, span: 760 },
    devSpeed: 1,
  };
}

export function held(state: GameState, id: string): number {
  return state.run.hold[id] ?? 0;
}

/** Coloured glyph for a resource: a dot for ores and alloys, its own glyph for items. */
export function resGlyph(id: string): string {
  const def = resource(id);
  if (def.kind === 'item') return `<span class="vm-glyph" style="color:${def.color}">${def.glyph}</span>`;
  const shape = def.kind === 'alloy' ? 'border-radius:2px' : '';
  return `<span class="vm-dot" style="background:${def.color};${shape}"></span>`;
}

export function resName(id: string): string {
  return `${resGlyph(id)}${esc(resource(id).name)}`;
}

/** "20/20 Ferrite · 4/10 Silica", red where short. */
export function ingredients(state: GameState, inputs: readonly Ingredient[]): string {
  return inputs.map((i) => {
    const have = held(state, i.id);
    const short = have < i.qty;
    return `<span style="color:${short ? '#d99494' : 'var(--text-dim)'}">${fmtNum(Math.min(have, 1e15))}/${i.qty} ${esc(resource(i.id).name)}</span>`;
  }).join(' · ');
}

export function attr(name: string, value: string | number): string {
  return `data-${name}="${esc(String(value))}"`;
}

export function btn(label: string, act: string, opts: {
  cls?: string; disabled?: boolean; data?: Record<string, string | number>;
} = {}): string {
  const data = Object.entries(opts.data ?? {}).map(([k, v]) => attr(k, v)).join(' ');
  return `<button class="vm-btn ${opts.cls ?? ''}" data-act="${act}" ${data}${opts.disabled ? ' disabled' : ''}>${label}</button>`;
}
